<template>
  <div v-if="!showLoginRequired" class="bets-table-wrapper">
    <el-table
      :data="tableData"
      class="bets-table"
      :class="anyModalActive ? 'opaque' : null"
      :table-layout="$device.isMobile ? 'auto' : 'fixed'"
      :span-method="
        ({ row, column, rowIndex, columnIndex }) => ($device.isMobile && columnIndex === 0 ? [1, 2] : [1, 1])
      "
      :show-header="false"
      :empty-text="$t('coreNoData')"
      :row-class-name="(data) => (isMe(data.row.ownerPlayer.userAddress) ? 'tr-own' : '')"
      style="--el-table-bg-color: none"
    >
      <el-table-column prop="userData" label="userData" min-width="100">
        <template #default="scope">
          <div class="user-data-mobile">
            <div class="avatar-wrapped">
              <el-avatar class="avatar" :size="54" :src="scope.row.ownerPlayer.userAvatarUrl" />
              <TavernFlipCoinIcon class="coinIcon" :is-odd="scope.row.isOddBet" />
            </div>

            <div v-if="$device.isMobile" class="bet-container">
              <div class="bet-value-container">
                <TavernFlipCoinPointsForBet
                  v-if="isMe(scope.row.ownerPlayer.userAddress) && $device.isMobile"
                  style="background: linear-gradient(to bottom, #4e3127, #000000)"
                  label-text=" "
                  :points-for-bet="
                    getSPAmount(
                      BigNumber(parseUnits(scope.row.betAmount.toString(), 'ether').toString() || '0')
                        .plus(currentUserGamesMappedWithLastSP?.find((g) => g.gameId === scope.row.gameId)?.lastSP?.[0])
                        .toNumber(),
                      tokensConfig?.find((t) => t.tokenAddress === scope.row.tokenAddress)?.seasonPointsMultiplier || 0,
                      currentUserGamesMappedWithLastSP?.find((g) => g.gameId === scope.row.gameId)?.lastSP?.[2] || 0
                    )
                  "
                />
                <img
                  class="icon-image"
                  :src="
                    tokens[
                      tokenMetaData(blockchain.contracts, scope.row.tokenAddress).name.contract as Tokens
                    ].toString()
                  "
                  width="32"
                  loading="lazy"
                  alt="Pearl Token Icon"
                />
                <span class="bet-value">{{ formatAmount(BigNumber(scope.row.betAmount).toNumber()) }}</span>
              </div>
            </div>
          </div>
          <div v-if="$device.isMobile" class="username-mobile">
            <div class="user-name">
              {{
                scope.row.ownerPlayer.userAddress?.includes(scope.row.ownerPlayer.userName)
                  ? formatAddress(scope.row.ownerPlayer.userAddress)
                  : scope.row.ownerPlayer.userName
              }}
            </div>

            <div
              v-if="$device.isMobile"
              v-loading="
                scope.row.gameId && scope.row.gameId === loadingGameId && scope.row.status !== GameStatus.STARTED
              "
              class="confirm-button-container"
            >
              <button
                v-if="scope.row.status === GameStatus.PENDING && !isMe(scope.row.ownerPlayer.userAddress)"
                class="btn-outline btn-confirm-bet"
                :style="{ whiteSpace: $i18n.locale === 'en' ? 'nowrap' : 'break-spaces' }"
                :disabled="loadingGameId != null"
                @click="confirmBetModalOpen(scope.row)"
              >
                {{ $t('confirmBetButton') }}
              </button>
              <button
                v-else-if="isMe(scope.row.ownerPlayer.userAddress)"
                class="btn-outline btn-cancel-bet"
                :style="{ whiteSpace: $i18n.locale === 'en' ? 'nowrap' : 'break-spaces' }"
                :disabled="loadingGameId != null || !scope.row.gameId || scope.row.status === GameStatus.STARTED"
                @click="cancelBetModalOpen(scope.row.gameId)"
              >
                <span>{{ $t('cancelBetButton') }}</span>
                <img src="/img/icons/cancel.svg" alt="cancel-icon" />
              </button>
              <div
                v-if="scope.row.status === GameStatus.STARTED && !isMe(scope.row.ownerPlayer.userAddress)"
                class="bet-accepted-container"
              >
                <img src="/img/icons/dice.svg" alt="dice-icon" />
                <span>{{ $t('acceptedBet') }}</span>
              </div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column v-if="!$device.isMobile" width="220" :resizable="true">
        <template #default="scope">
          <div class="user-name">
            {{
              scope.row.ownerPlayer.userAddress?.includes(scope.row.ownerPlayer.userName)
                ? formatAddress(scope.row.ownerPlayer.userAddress)
                : scope.row.ownerPlayer.userName
            }}
          </div>
        </template>
      </el-table-column>
      <el-table-column v-if="!$device.isMobile" min-width="120">
        <template #default="scope">
          <div>
            <TavernFlipCoinPointsForBet
              v-if="isMe(scope.row.ownerPlayer.userAddress)"
              style="background: linear-gradient(to bottom, #4e3127, #000000)"
              label-text="+"
              :points-for-bet="
                getSPAmount(
                  BigNumber(parseUnits(scope.row.betAmount.toString(), 'ether').toString() || '0')
                    .plus(currentUserGamesMappedWithLastSP?.find((g) => g.gameId === scope.row.gameId)?.lastSP?.[0])
                    .toNumber(),
                  tokensConfig?.find((t) => t.tokenAddress === scope.row.tokenAddress)?.seasonPointsMultiplier || 0,
                  currentUserGamesMappedWithLastSP?.find((g) => g.gameId === scope.row.gameId)?.lastSP?.[2] || 0
                )
              "
            />
          </div>
        </template>
      </el-table-column>
      <el-table-column v-if="!$device.isMobile" prop="betAmount" label="bet" min-width="180">
        <template #default="scope">
          <div class="bet-container">
            <span class="bet-prefix">{{ $t('coreBet') }}:</span>
            <div class="bet-value-container">
              <img
                class="icon-image"
                :src="
                  tokens[tokenMetaData(blockchain.contracts, scope.row.tokenAddress).name.contract as Tokens].toString()
                "
                width="32"
                loading="lazy"
                alt="Pearl Token Icon"
              />
              <span class="bet-value">{{ formatAmount(BigNumber(scope.row.betAmount).toNumber()) }}</span>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column v-if="!$device.isMobile" prop="status" label="confirm-bet" min-width="180">
        <template #default="scope">
          <div
            v-loading="
              scope.row.gameId && scope.row.gameId === loadingGameId && scope.row.status !== GameStatus.STARTED
            "
            class="confirm-button-container"
          >
            <button
              v-if="
                (scope.row.status === GameStatus.PENDING || scope.row.status === GameStatus.INITIAL) &&
                !isMe(scope.row.ownerPlayer.userAddress)
              "
              class="btn-outline btn-confirm-bet"
              :disabled="loadingGameId != null || scope.row.status === GameStatus.INITIAL"
              @click="confirmBetModalOpen(scope.row)"
            >
              {{ $t('confirmBetButton') }}
            </button>
            <button
              v-else-if="isMe(scope.row.ownerPlayer.userAddress)"
              class="btn-outline btn-cancel-bet"
              :disabled="loadingGameId != null || !scope.row.gameId || scope.row.status === GameStatus.STARTED"
              @click="cancelBetModalOpen(scope.row.gameId)"
            >
              <span>{{ $t('cancelBetButton') }}</span>
              <img src="/img/icons/cancel.svg" alt="cancel-icon" />
            </button>
            <div
              v-if="scope.row.status === GameStatus.STARTED && !isMe(scope.row.ownerPlayer.userAddress)"
              class="bet-accepted-container"
            >
              <img src="/img/icons/dice.svg" alt="dice-icon" />
              <span>{{ $t('acceptedBet') }}</span>
            </div>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
  <TavernFlipCoinLoginRequired v-if="showLoginRequired" @get-out="$emit('back-to-bets')" />
  <TavernFlipCoinBetConfirmationModal
    v-if="showConfirmBet"
    :game-data="gameDataRef"
    @confirm-bet="confirmBetHandler"
    @get-out="showConfirmBet = false"
  />

  <TavernFlipCoinBetTakenModal v-if="showTakenBet" @get-out="showTakenBet = false" />
  <TavernFlipCoinBetCancelModal v-if="showCancelBet" @cancel-bet="cancelBet" @get-out="showCancelBet = false" />
</template>

<script lang="ts" setup>
import { GameStatus, type FlipCoinGame } from '~/types/FlipCoin';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { apiUrls, SortOptions, Tokens } from '~/utils/constants';
import { BigNumber } from 'bignumber.js';
import tokens from '~/public/img/tokens';
import { formatEther, getAddress, parseUnits } from 'ethers';
import type { IFlipCoinTokenConfig } from '~/types/apiService';

defineProps<{
  loadingGameId: number | null;
}>();

const { address } = useWeb3ModalAccount();
const { blockchain, apiUrl } = useEnvs();
const { getLastAddedSPTimeAndMaxSP } = useFlipCoinMethods();
const emit = defineEmits(['cancel-game', 'confirm-bet-handler', 'back-to-bets', 'update-points']);
const { $websockets: websockets } = useNuxtApp();
const flipCoinSocket = websockets?.['flip-coin-game'];

const gameDataRef = ref(null as FlipCoinGame | null);
const cancelGameId = ref(null as number | null);
const showLoginRequired = inject<Ref<boolean>>('showLoginRequired');
const currentSortOption = inject<Ref<SortOptions>>('currentSortOption');
const showConfirmBet = inject<Ref<boolean>>('showConfirmBet', ref(false));
const showCancelBet = inject<Ref<boolean>>('showCancelBet', ref(false));
const showTakenBet = inject<Ref<boolean>>('showTakenBet', ref(false));
const anyModalActive = inject<Ref<boolean>>('anyModalActive', ref(false));

const { data: tokensConfig } = useNuxtData<IFlipCoinTokenConfig[]>('tokensList');

const tableData = computed<FlipCoinGame[]>(() =>
  flipCoinSocket.parsedData
    .filter((item: FlipCoinGame) => item.status !== GameStatus.CANCELED && item.status !== GameStatus.COMPLETED)
    .sort((a: FlipCoinGame, b: FlipCoinGame) =>
      SortCompareCallbacks[currentSortOption?.value ?? SortOptions.DATE](
        currentSortOption?.value === SortOptions.DATE
          ? new Date(a.createdAt).getTime()
          : BigNumber(a.betAmount).toNumber(),
        currentSortOption?.value === SortOptions.DATE
          ? new Date(b.createdAt).getTime()
          : BigNumber(b.betAmount).toNumber()
      )
    )
);

const currentUserGames = computed(() => {
  if (!address?.value) return [];
  return tableData.value?.filter(
    (item: FlipCoinGame) => getAddress(address.value!) === getAddress(item.ownerPlayer.userAddress)
  );
});

const currentUserGamesTokensSet = computed(
  () => new Set(currentUserGames.value?.map((item: FlipCoinGame) => item.tokenAddress))
);

const uniqueTokens = computed(() => Array.from(currentUserGamesTokensSet.value)?.map((item) => item));

const lastSPInCurrentPeriodPerTokenForCurrentUser = asyncComputed(async () => {
  return await Promise.all<any>(uniqueTokens.value?.map((item) => getLastAddedSPTimeAndMaxSP(item)));
}, []);

const currentUserGamesMappedWithLastSP = computed(() => {
  return currentUserGames.value?.map((item: FlipCoinGame) => {
    return {
      ...item,
      lastSP:
        lastSPInCurrentPeriodPerTokenForCurrentUser.value[
          uniqueTokens.value.findIndex((token) => token === item.tokenAddress)
        ]
    };
  });
});

const pointsSumForCurrentUser = computed(() => {
  let total = 0;
  if (currentUserGames.value?.length === 0) return 0;

  uniqueTokens.value?.forEach((item, index) => {
    const mappedGamesPerToken = currentUserGamesMappedWithLastSP.value
      .filter((game) => game.tokenAddress === item && !!lastSPInCurrentPeriodPerTokenForCurrentUser.value?.[index])
      .sort((a: FlipCoinGame, b: FlipCoinGame) => {
        return BigNumber(a.betAmount).isGreaterThan(b.betAmount) ? 1 : -1;
      });

    mappedGamesPerToken.reduce(
      (acc, game) => {
        const spAmount = getSPAmount(
          BigNumber(parseUnits(game.betAmount.toString(), 'ether').toString())
            .plus(acc.lastSP?.[0] || 0)
            .toNumber(),
          tokensConfig.value?.find((t) => t.tokenAddress === game.tokenAddress)?.seasonPointsMultiplier || 0,
          BigNumber(acc.lastSP?.[2]).toNumber()
        );

        total += spAmount;

        return {
          ...acc,
          lastSP: [
            parseUnits(
              BigNumber(formatEther(acc.lastSP?.[0] || 0))
                .plus(game.betAmount)
                .toString(),
              'ether'
            ),
            acc.lastSP?.[1],
            BigNumber(acc.lastSP?.[2]).plus(spAmount)
          ]
        };
      },
      { ...mappedGamesPerToken?.[0] }
    );
  });
  return total;
});

watch(
  [pointsSumForCurrentUser, currentUserGames],
  () => {
    emit('update-points', currentUserGames.value?.length ? pointsSumForCurrentUser.value : 0);
  },
  { immediate: true }
);

const isMe = (betAddress: string) => {
  if (!address.value || !betAddress) return false;
  return getAddress(address.value) === getAddress(betAddress);
};

const cancelBet = () => {
  showCancelBet.value = false;
  emit('cancel-game', cancelGameId.value);
};

const cancelBetModalOpen = (gameId: number) => {
  if (!address?.value) {
    emit('cancel-game', gameId);
    return;
  }

  cancelGameId.value = gameId;
  showCancelBet.value = true;
};

const confirmBetModalOpen = (gameData: FlipCoinGame) => {
  if (!address?.value) {
    emit('confirm-bet-handler', gameData);
    return;
  }
  $fetch(apiUrls.flipCoinGame.confirmGame(gameData.gameId), {
    method: 'PUT',
    baseURL: apiUrl
  });
  gameDataRef.value = gameData;
  showConfirmBet.value = true;
};

const confirmBetHandler = () => {
  showConfirmBet.value = false;
  emit('confirm-bet-handler', gameDataRef.value);
};
</script>

<style lang="scss">
.el-loading-mask {
  background: transparent;
}
</style>

<style lang="scss" scoped>
.avatar-wrapped {
  width: 54px;
  position: relative;
  @media screen and (max-width: 479px) {
    margin-right: 20px;
  }

  .avatar {
    border: 2px solid #201a0e;
  }

  .coinIcon {
    position: absolute;
    width: 32px;
    height: 32px;
    left: 43px;
    top: 12px;
  }
}

.btn-outline {
  height: 45px;
  font-size: 18px;
  font-weight: 600;
  font-family: Eczar;
  padding: 0 20px;
}

.btn-confirm-bet {
  border: solid 2px #684420;
  background: linear-gradient(180deg, rgba(221, 177, 104, 1) 0%, rgba(135, 70, 24, 1) 50%, rgba(12, 4, 5, 1) 100%);
}

.btn-cancel-bet {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-confirm-bet,
.btn-cancel-bet {
  @media screen and (max-width: 479px) {
    white-space: break-spaces;
  }
  &:disabled {
    opacity: 0.7;
    pointer-events: none;
    background: #a2a2a2;
    color: #000;
    border: 1px solid #fff;
  }
}

.bet-prefix {
  font-family: Eczar;
  font-weight: 600;
  font-size: 20px;
  line-height: 26.4px;
  color: #a2a2a2;
}

.user-name {
  font-family: Eczar;
  font-weight: 600;
  font-size: 20px;
  line-height: 26.4px;
  color: #a2a2a2;
  justify-self: flex-start;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
}

.bet-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;

  @media screen and (max-width: 479px) {
    width: max-content;
  }

  .bet-value-container {
    color: #fff;
    display: flex;
    align-items: center;
  }

  .bet-value {
    font-family: Eczar, sans-serif;
    font-weight: 600;
    font-size: 24px;
    line-height: 26.4px;
    white-space: nowrap;

    @media screen and (max-width: 479px) {
      padding-right: 5px;
    }
  }
}

.confirm-button-container {
  justify-self: flex-end;

  @media (hover: hover) {
    .btn-outline:hover {
      background-image: none;
    }
  }
}

.bet-accepted-container {
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    font-family: Eczar;
    font-weight: 600;
    font-size: 18px;
    line-height: 18px;
    color: #12f83a;
    white-space: break-spaces;
    text-align: center;
  }
}

.user-data-mobile {
  display: flex;

  @media screen and (max-width: 479px) {
    align-items: center;
    justify-content: space-between;
    gap: 15px;
  }
}

.username-mobile {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 5px;
}
</style>

<style lang="scss">
.bets-table-wrapper {
  @media screen and (max-width: 767px) {
    display: contents;
  }

  @media screen and (min-width: 768px) {
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    padding-right: 4px;

    &::-webkit-scrollbar {
      width: 8px !important;
    }

    &::-webkit-scrollbar-button {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
      background: transparent !important;
    }

    &::-webkit-scrollbar-button:start:decrement,
    &::-webkit-scrollbar-button:end:increment {
      display: none !important;
    }

    &::-webkit-scrollbar-track {
      background: transparent !important;
      margin: 12px 0 !important;
    }

    &::-webkit-scrollbar-thumb {
      background: #a49686 !important;
      border-radius: 5px !important;
      min-height: 40px !important;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #b8aa9c !important;
    }

    &::-webkit-scrollbar-corner {
      background: transparent !important;
    }
  }
}

.bets-table.opaque {
  opacity: 0.2;
  pointer-events: none;
}

.bets-table.el-table {
  margin-bottom: 10px;

  @media screen and (max-width: 479px) {
    width: 99vw;
    max-width: 100vw;
    margin-left: -25px;
    height: 100%;
  }

  @media screen and (min-width: 768px) {
    height: auto !important;
    max-height: none !important;

    .el-table__body-wrapper {
      overflow: visible !important;
      max-height: none !important;
    }

    .el-table__inner-wrapper {
      overflow: visible !important;
      max-height: none !important;
    }
  }

  .cell {
    padding: 0;
  }

  table {
    border-spacing: 0px 6px;
  }

  /* For correct box-shadow effect on hover tr */
  /* .el-table__body {
    margin-left: 5px;
    margin-right: 5px;
  } */

  tr {
    background: linear-gradient(to bottom, rgba(78, 49, 39, 1), rgba(0, 0, 0, 1));
    border-radius: 12px;
  }

  td.el-table__cell {
    border-top: 1px solid #483b20;
    border-bottom: 1px solid #483b20;
    height: 65px;
    padding: 1.5px 0;
  }

  td.el-table__cell:first-child {
    border-left: 1px solid #483b20;
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
    padding-left: 16px;
  }

  td.el-table__cell:last-child {
    border-right: 1px solid #483b20;
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
    padding-right: 16px;
  }

  td.el-table__cell:first-child,
  td.el-table__cell:last-child {
    @media screen and (max-width: 479px) {
      padding: 0 4px;
    }
  }

  tr:hover {
    :deep(td.el-table__cell) {
      background-color: unset;
    }
    background: linear-gradient(to bottom, rgba(78, 49, 39, 1), rgba(0, 0, 0, 1));
  }

  .tr-own {
    background: linear-gradient(180deg, rgba(221, 177, 104, 1) 0%, rgba(135, 70, 24, 1) 50%, rgba(12, 4, 5, 1) 100%);
    box-shadow: 0 0 5px 0 #ddcead;

    .avatar {
      border: 2px solid #b87d2b;
    }

    &:hover {
      td.el-table__cell {
        background-color: unset;
      }
      background: linear-gradient(180deg, rgba(221, 177, 104, 1) 0%, rgba(135, 70, 24, 1) 50%, rgba(12, 4, 5, 1) 100%);
    }

    .user-name {
      color: #fff;
    }

    td.el-table__cell {
      border-top: 1px solid #b87d2b;
      border-bottom: 1px solid #b87d2b;
      height: 65px;
    }

    td.el-table__cell:first-child {
      border-left: 1px solid #b87d2b;
      border-top-left-radius: 12px;
      border-bottom-left-radius: 12px;
    }

    td.el-table__cell:last-child {
      border-right: 1px solid #b87d2b;
      border-top-right-radius: 12px;
      border-bottom-right-radius: 12px;
      padding-right: 16px;
    }
  }

  .el-table__empty-text {
    font-size: 22px;
  }
}

.el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
  background-color: unset !important;
}

.el-table__inner-wrapper:before {
  display: none;
}
</style>
